import React, { useState } from 'react';

interface BugCase {
  id: number;
  title: string;
  severity: 'Critical' | 'High' | 'Medium';
  environment: string;
  symptom: string;
  rootCause: string;
  fix: string;
  impact: string;
  code?: { before: string; after: string };
}

const BUG_CASES: BugCase[] = [
  {
    id: 1,
    title: 'Memory Leak in React useEffect',
    severity: 'Critical',
    environment: 'React 18 / Node.js',
    symptom:
      'Browser tab memory growing unbounded in a real-time dashboard. After 30 mins: 1.2 GB RAM, UI freezes.',
    rootCause:
      'WebSocket event listeners were re-registered on every render without cleanup. The useEffect had a dependency on a callback defined inline, causing a new subscription on every state update.',
    fix: 'Wrapped callback in useCallback, memoized it with stable deps. Added cleanup function returning ws.off() in useEffect. Moved the WS instance to a ref to avoid re-creation.',
    impact: 'Memory stabilised at ~45 MB regardless of session length. Zero freezes in production.',
    code: {
      before: `// ❌ Before — new listener on every render
useEffect(() => {
  socket.on('data', (d) => setData(d));
}, [data]); // 'data' in deps = infinite subscriptions`,
      after: `// ✅ After — single listener, proper cleanup
const handleData = useCallback((d: DataType) => {
  setData(d);
}, []);

useEffect(() => {
  socket.on('data', handleData);
  return () => socket.off('data', handleData);
}, [handleData]);`,
    },
  },
  {
    id: 2,
    title: 'N+1 Query in MongoDB Aggregation',
    severity: 'High',
    environment: 'Node.js / MongoDB / Express',
    symptom:
      'API endpoint timing out at ~12 seconds for a list of 200 orders. CPU spiking on DB server.',
    rootCause:
      'Inside a .find() loop, a separate DB call fetched each order\'s user details — 200 orders = 201 DB round-trips.',
    fix: 'Replaced loop + individual queries with a single $lookup aggregation pipeline joining orders and users collections in one pass.',
    impact: 'Response time dropped from 12s → 180ms. DB CPU usage reduced by 82%.',
    code: {
      before: `// ❌ Before — N+1 queries
const orders = await Order.find({ status: 'pending' });
for (const order of orders) {
  order.user = await User.findById(order.userId); // DB call per order!
}`,
      after: `// ✅ After — single aggregation
const orders = await Order.aggregate([
  { $match: { status: 'pending' } },
  {
    $lookup: {
      from: 'users',
      localField: 'userId',
      foreignField: '_id',
      as: 'user',
    },
  },
  { $unwind: '$user' },
]);`,
    },
  },
  {
    id: 3,
    title: 'Race Condition in Async State Updates',
    severity: 'Medium',
    environment: 'React / REST API',
    symptom:
      'Search results occasionally flash wrong data — typing quickly would show stale results from a slow earlier request overwriting faster later ones.',
    rootCause:
      'No request cancellation. If Request A (slow) responded after Request B (fast), it would overwrite the correct result.',
    fix: 'Used AbortController to cancel previous fetch on each new keystroke. Paired with debounce to reduce total requests.',
    impact: 'Eliminated stale result flashing entirely. API request volume reduced by ~70% via debounce.',
    code: {
      before: `// ❌ Before — race condition
useEffect(() => {
  fetch(\`/api/search?q=\${query}\`)
    .then(r => r.json())
    .then(setResults); // may arrive out of order!
}, [query]);`,
      after: `// ✅ After — AbortController + debounce
useEffect(() => {
  const controller = new AbortController();
  const timer = setTimeout(async () => {
    try {
      const res = await fetch(\`/api/search?q=\${query}\`, {
        signal: controller.signal,
      });
      const data = await res.json();
      setResults(data);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') console.error(e);
    }
  }, 300);

  return () => {
    clearTimeout(timer);
    controller.abort();
  };
}, [query]);`,
    },
  },
];

const SEVERITY_COLORS: Record<BugCase['severity'], string> = {
  Critical: 'text-red-400 border-red-500/30 bg-red-500/5',
  High: 'text-amber-400 border-amber-500/30 bg-amber-500/5',
  Medium: 'text-blue-400 border-blue-500/30 bg-blue-500/5',
};

const DebugSection: React.FC = () => {
  const [active, setActive] = useState<number>(1);
  const [tab, setTab] = useState<'before' | 'after'>('before');

  const bug = BUG_CASES.find((b) => b.id === active)!;

  return (
    <section id="debugging" className="py-28 bg-[#0a0a0f]">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16">
          <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">
            Real-world Fixes
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white">Bug Fixing</h2>
          <p className="text-slate-400 mt-3 max-w-xl">
            Production incidents I've diagnosed and resolved — root cause, fix, and measurable
            impact.
          </p>
        </div>

        {/* Case selector */}
        <div className="flex flex-wrap gap-3 mb-8">
          {BUG_CASES.map((b) => (
            <button
              key={b.id}
              onClick={() => { setActive(b.id); setTab('before'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                active === b.id
                  ? 'bg-red-500/10 border-red-500/40 text-red-300'
                  : 'bg-[#13131e] border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {b.title}
            </button>
          ))}
        </div>

        {/* Bug card */}
        <div className="rounded-2xl bg-[#13131e] border border-slate-800 overflow-hidden">
          {/* Card header */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-b border-slate-800">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-xs font-mono text-slate-500">{bug.environment}</span>
                <span
                  className={`px-2 py-0.5 rounded-full border text-xs font-mono ${
                    SEVERITY_COLORS[bug.severity]
                  }`}
                >
                  {bug.severity}
                </span>
              </div>
              <h3 className="text-xl font-bold text-white">{bug.title}</h3>
            </div>
          </div>

          {/* Body */}
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-800">
            {/* Left: analysis */}
            <div className="p-6 space-y-5">
              <div>
                <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">
                  Symptom
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{bug.symptom}</p>
              </div>
              <div>
                <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">
                  Root Cause
                </div>
                <p className="text-sm text-amber-300/80 leading-relaxed">{bug.rootCause}</p>
              </div>
              <div>
                <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">
                  Fix Applied
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{bug.fix}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="text-xs font-mono text-emerald-400 uppercase tracking-widest mb-1">
                  Impact
                </div>
                <p className="text-sm text-emerald-300">{bug.impact}</p>
              </div>
            </div>

            {/* Right: code diff */}
            {bug.code && (
              <div className="p-6">
                <div className="flex gap-1 mb-4 bg-slate-900 rounded-lg p-1 w-fit">
                  {(['before', 'after'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      className={`px-4 py-1.5 rounded-md text-xs font-mono font-semibold uppercase tracking-wider transition-all duration-150 ${
                        tab === t
                          ? t === 'before'
                            ? 'bg-red-500/20 text-red-300'
                            : 'bg-emerald-500/20 text-emerald-300'
                          : 'text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {t === 'before' ? '❌ Before' : '✅ After'}
                    </button>
                  ))}
                </div>
                <pre className="text-xs font-mono text-slate-300 leading-relaxed overflow-x-auto bg-slate-950/60 rounded-lg p-4 border border-slate-800/60 min-h-[200px]">
                  <code>{tab === 'before' ? bug.code.before : bug.code.after}</code>
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DebugSection;
